// Register Page
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, Lock, User, AlertCircle, Globe, CheckCircle } from 'lucide-react';

export default function RegisterPage() {
  const { register, error } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (password !== confirmPassword) {
      setLocalError(language === 'el' ? 'Οι κωδικοί δεν ταιριάζουν' : 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setLocalError(language === 'el' ? 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες' : 'Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(name, email, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setLocalError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#E6007E] to-[#b8005f]">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_facilitate-score/artifacts/58269d0v_IMG_9526.jpg')`
          }}
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-8">
            <span className="text-3xl font-bold">G</span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-4">
            {language === 'el' ? 'Γίνε μέλος της ομάδας' : 'Join the Team'}
          </h1>
          <p className="text-xl text-white/80 mb-8">
            {language === 'el' 
              ? 'Δημιούργησε λογαριασμό και ξεκίνα την αξιολόγηση'
              : 'Create an account and start your evaluation journey'
            }
          </p>
          <div className="space-y-3 text-white/70">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span>{language === 'el' ? 'Παρακολούθηση βαθμολογίας' : 'Track your scores'}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span>{language === 'el' ? 'Αυτοαξιολόγηση & αξιολόγηση ομοτίμων' : 'Self & peer evaluations'}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span>{language === 'el' ? 'Βραβεία MVP & αναγνώριση' : 'MVP awards & recognition'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          {/* Language Toggle */}
          <div className="flex justify-end mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage}
              className="text-gray-500 hover:text-[#E6007E]"
              data-testid="language-toggle"
            >
              <Globe size={18} className="mr-2" />
              {language === 'el' ? 'English' : 'Ελληνικά'}
            </Button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E6007E] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">G</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-[#2A1514]">GrowPlay</h1>
              <p className="text-sm text-gray-500">Facilitator Hub</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-heading text-2xl text-[#2A1514]">
                {t('register')}
              </CardTitle>
              <CardDescription>
                {language === 'el' 
                  ? 'Δημιουργήστε νέο λογαριασμό'
                  : 'Create a new account'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {success && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    {language === 'el' 
                      ? 'Εγγραφή επιτυχής! Ανακατεύθυνση...'
                      : 'Registration successful! Redirecting...'
                    }
                  </AlertDescription>
                </Alert>
              )}

              {(error || localError) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error || localError}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="name"
                      type="text"
                      placeholder={language === 'el' ? 'Γιώργος Παπαδόπουλος' : 'John Doe'}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#E6007E] hover:bg-[#b8005f] text-white font-medium"
                  disabled={loading || success}
                  data-testid="register-submit-btn"
                >
                  {loading ? t('loading') : t('signUp')}
                </Button>
              </form>

              <p className="text-center text-sm text-gray-500">
                {t('alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-[#E6007E] hover:underline font-medium" data-testid="login-link">
                  {t('signIn')}
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
